import React, { useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Seo from "../../components/Seo";
import { LoadMore, Modal, PageHeader, SegmentTabs, Spinner, usePagedList } from "../../components/lawyer/LawyerUi";
import { apiErrorMessage, lawyerService } from "../../services/lawyerApi";

const names = (list) => (Array.isArray(list) ? list.map((i) => i?.name).filter(Boolean) : []);
const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const fileNameOf = (url) => decodeURIComponent((url || "").split("/").pop() || "Attachment");

const RequestsEmpty = ({ text }) => (
  <div className="lwp-illustrated-empty">
    <div className="lwp-empty-art" aria-hidden="true">
      <i className="bi bi-clipboard2-check" />
      <i className="bi bi-bank2" />
    </div>
    <p>{text}</p>
  </div>
);

/* ---------------- Cases ---------------- */

const CaseDetails = ({ item, onClose, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const interested = Number(item.is_interested) > 0;

  const toggleInterest = async () => {
    setBusy(true);
    try {
      const res = await lawyerService.toggleCaseInterest(item.id);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      onChange({ ...item, is_interested: interested ? 0 : 1 });
      toast.success(res.data.message || (interested ? "Interest withdrawn" : "Client notified of your interest"));
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to update interest."));
    } finally {
      setBusy(false);
    }
  };

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const res = await lawyerService.sendCaseAttachments(item.id, files);
      if (!res?.data?.status) throw new Error(res?.data?.message);
      toast.success(res.data.message || "Documents shared with the client");
      setFiles([]);
    } catch (err) {
      toast.error(apiErrorMessage(err, err?.message || "Unable to upload documents."));
    } finally {
      setUploading(false);
    }
  };

  const rows = [
    ["Service", item.type_legal_consultant],
    ["Consultation", asList(item.consultation_type).join(", ")],
    ["Category", [...names(item.categories), ...names(item.sub_categories)].join(", ")],
    ["Jurisdiction", names(item.jurisdictions).join(", ")],
    ["Case Value", item.case_value ? `${item.case_value}` : null],
    ["Budget", item.case_budget],
    ["Languages", asList(item.languages).join(", ")],
  ].filter(([, v]) => v);

  return (
    <Modal
      open
      onClose={onClose}
      title={`Request #${item.id}`}
      footer={
        <button
          type="button"
          className={`lwp-btn${interested ? " lwp-btn-outline lwp-btn-block" : ""}`}
          onClick={toggleInterest}
          disabled={busy}
        >
          {busy ? <span className="spinner-border spinner-border-sm" /> : interested ? "Withdraw Interest" : "I'm Interested"}
        </button>
      }
    >
      {interested && (
        <p className="lwp-note-ok">
          <i className="bi bi-check-circle-fill" /> You've shown interest. The client has been notified.
        </p>
      )}
      <p className="lwp-case-desc">{item.description}</p>

      <dl className="lwp-detail-list">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      {asList(item.attachments).length > 0 && (
        <>
          <h3 className="lwp-call-details-title">Client Attachments</h3>
          <div className="lwp-file-chips">
            {item.attachments.map((url) => (
              <a key={url} href={url} target="_blank" rel="noreferrer" className="lwp-file-chip">
                <i className="bi bi-paperclip" /> {fileNameOf(url)}
              </a>
            ))}
          </div>
        </>
      )}

      {interested && (
        <>
          <h3 className="lwp-call-details-title">Share Documents</h3>
          <p className="lwp-muted lwp-small">Send your proposal, engagement letter or other documents to the client.</p>
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              setFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
          {files.length > 0 && (
            <div className="lwp-file-chips">
              {files.map((f) => (
                <span key={f.name} className="lwp-file-chip">
                  <i className="bi bi-file-earmark" /> {f.name}
                </span>
              ))}
            </div>
          )}
          <div className="lwp-inline-actions">
            <button type="button" className="lwp-link-btn" onClick={() => fileRef.current?.click()}>
              <i className="bi bi-plus-circle" /> {files.length ? "Choose different files" : "Choose files"}
            </button>
            {files.length > 0 && (
              <button type="button" className="lwp-btn lwp-btn-sm lwp-btn-auto" onClick={upload} disabled={uploading}>
                {uploading ? <span className="spinner-border spinner-border-sm" /> : `Send ${files.length} file${files.length > 1 ? "s" : ""}`}
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

const CasesTab = () => {
  const list = usePagedList((p) => lawyerService.getAllCases(p), (d) => d.cases, []);
  const [selected, setSelected] = useState(null);

  if (list.loading) return <Spinner />;
  if (list.meta.error) return <RequestsEmpty text={list.meta.error} />;
  if (!list.items.length) return <RequestsEmpty text="No requests match your practice areas yet. New cases will show up here." />;

  const update = (next) => {
    list.setItems((prev) => prev.map((c) => (c.id === next.id ? next : c)));
    setSelected(next);
  };

  return (
    <>
      <ul className="lwp-card-list lwp-request-grid">
        {list.items.map((c) => (
          <li key={c.id}>
            <button type="button" className="lwp-request-card" onClick={() => setSelected(c)}>
              <span className="lwp-request-top">
                <span className="lwp-id-pill">#{c.id}</span>
                {Number(c.is_interested) > 0 && <span className="lwp-status lwp-status-ended">Interested</span>}
              </span>
              {c.type_legal_consultant && <strong className="lwp-request-type">{c.type_legal_consultant}</strong>}
              <span className="lwp-request-text">{c.description}</span>
              {names(c.jurisdictions).length > 0 && (
                <span className="lwp-request-meta">
                  <b>Jurisdiction :</b> {names(c.jurisdictions).join(", ")}
                </span>
              )}
              {names(c.categories).length > 0 && (
                <span className="lwp-tag-row">
                  {names(c.categories).map((n) => (
                    <span key={n} className="lwp-tag">{n}</span>
                  ))}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
      <LoadMore list={list} />
      {selected && <CaseDetails item={selected} onClose={() => setSelected(null)} onChange={update} />}
    </>
  );
};

/* ---------------- Questions ---------------- */

const QuestionsTab = () => {
  const list = usePagedList((p) => lawyerService.getAllQuestions(p), (d) => d.questions, []);

  if (list.loading) return <Spinner />;
  if (list.meta.error) return <RequestsEmpty text={list.meta.error} />;
  if (!list.items.length) return <RequestsEmpty text="No questions yet." />;

  return (
    <>
      <ul className="lwp-card-list lwp-request-grid">
        {list.items.map((q) => {
          const when = moment(q.created_at);
          return (
            <li key={q.id}>
              <Link to={`/lawyer/requests/questions/${q.id}`} className={`lwp-request-card${q.is_viewed ? "" : " unseen"}`}>
                <span className="lwp-request-top">
                  <span className="lwp-id-pill">#{q.id}</span>
                  {q.is_answered ? (
                    <span className="lwp-status lwp-status-ended">Answered</span>
                  ) : q.status === "Closed" ? (
                    <span className="lwp-status">Closed</span>
                  ) : !q.is_viewed ? (
                    <span className="lwp-status lwp-status-pending">New</span>
                  ) : null}
                </span>
                <span className="lwp-request-text">{q.question}</span>
                {q.jurisdiction?.name && (
                  <span className="lwp-request-meta">
                    <b>Jurisdiction :</b> {q.jurisdiction.name}
                  </span>
                )}
                {when.isValid() && (
                  <span className="lwp-meta lwp-request-date">
                    <i className="bi bi-calendar3" /> {when.format("DD MMM, YYYY")}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
      <LoadMore list={list} />
    </>
  );
};

const LawyerRequests = () => {
  const { profile } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "questions" ? "questions" : "requests";

  return (
    <div className="lwp-page">
      <Seo title="My Requests | Legal Platform" noindex />
      <PageHeader title="My Request" />
      <SegmentTabs
        label="Request type"
        value={tab}
        onChange={(key) => setParams(key === "questions" ? { tab: key } : {}, { replace: true })}
        tabs={[
          { key: "requests", label: "Request" },
          { key: "questions", label: "Questions", count: Number(profile?.question_unread) || 0 },
        ]}
      />
      <div className="lwp-tab-body">{tab === "questions" ? <QuestionsTab /> : <CasesTab />}</div>
    </div>
  );
};

export default LawyerRequests;
